import { merge } from 'lodash-es';
import { ACTION_TAG, ACTION_GLOBAL_TAG, ACTION_EXTRA_TAG, DEFAULT_REPORT_NODE } from '../../config/constants';

// 多组字段分割符
const PAIR_SPLIT = ';';

// 键值分割符
const KEY_VALUE_SPLIT = ':';

// 字段组正则：变量名:任意值
const PAIR_PATTERN = /^([a-zA-Z_]+\w*):(.+)$/;

// 是否是类json
const JSON_LIKE_PATTERN = /^\{.*\}$|^\[.*\]$/;

/**
 * 字符串参数转换成对象
 * @param str 字符串参数，使用PAIR_SPLIT分割
 * @return 对象
 */
export const paramsParser = (str = '') => {
  const items = str.split(PAIR_SPLIT);
  const validItems: Array<string> = [];
  // 分割字段
  items.forEach((v) => {
    let j = validItems.length;
    let cur = v;
    // 如果字段不合法，与前一个字段合并重新计算
    while (j >= 0) {
      if (PAIR_PATTERN.test(cur)) {
        validItems.splice(j, validItems.length - j, cur);
        break;
      } else {
        j -= 1;
        cur = `${validItems[j]}${PAIR_SPLIT}${cur}`;
      }
    }
  });

  return validItems.reduce((map, v) => ({ ...map, ...pairParse(v) }), {});
};

/**
 * 键值对字符串转换成对象
 * @param str 键值对字符串,使用KEY_VALUE_SPLIT分割
 * @return 对象
 */
const pairParse = (str = '') => {
  const [key, ...valueArr] = str.split(KEY_VALUE_SPLIT);
  const value = valueArr.join(KEY_VALUE_SPLIT);

  // 解析json
  if (isJSONLike(value)) {
    return { [key]: toJSON(value) };
  }

  // 布尔值
  if (value === 'true' || value === 'false') {
    return { [key]: Boolean(value) };
  }

  if (value && !isNaN(Number(value))) {
    return { [key]: Number(value) };
  }

  return { [key]: value };
};

/**
 * 是否可能是json
 * @param str 字符串
 * @returns
 */
const isJSONLike = (str = '') => JSON_LIKE_PATTERN.test(str);

/**
 * json解析，解析报错返回原字符串
 * @param str 字符串
 * @returns
 */
const toJSON = (str = '') => {
  try {
    return JSON.parse(str);
  } catch (error) {
    return str;
  }
};

/**
 * 字符串化
 * @param data 数据
 * @returns
 */
export const stringify = (data: any) => {
  if (typeof data === 'object') {
    return JSON.stringify(data);
  }
  return String(data);
};

/**
 * 获取元素唯一DOM路径
 * @param el 页面元素
 * @returns
 */
export const getDomPath = (el: Element) => {
  if (!el) {
    return '';
  }
  const stack: Array<string> = [];
  while (el.parentNode !== null) {
    let sibCount = 0;
    let sibIndex = 0;
    // get sibling indexes
    /*eslint-disable */
    el.parentNode.childNodes.forEach((sib) => {
      if (sib.nodeName === el.nodeName) {
        if (sib === el) {
          sibIndex = sibCount;
        }
        sibCount++;
      }
    });
    const nodeName = el.nodeName.toLowerCase();
    if (el.hasAttribute('id') && el.id != '') {
      stack.push(`${el.nodeName.toLowerCase()}#${el.id}`);
    } else if (sibCount > 1) {
      stack.push(`${nodeName}:nth-of-type(${sibIndex + 1})`);
    } else {
      stack.push(nodeName);
    }
    el = el.parentNode as Element;
  }
  stack.pop(); // removes the html element
  stack.reverse();
  return stack.join(' > ');
};

/**
 * 至少匹配一个选择器
 * @param el 元素
 * @param selectors 选择器列表
 * @returns
 */
export const matchesOne = (el: Element, selectors: Array<string>) => selectors.some(v => typeof el.matches === 'function' && el.matches(v));

/**
 * 获取上报信息
 * @param el 事件对象
 * @returns
 */
export const getReportInfo = (
  e: Event,
  options: {
    include?: Array<string>;
    exclude?: Array<string>;
    enableDefaultReport?: boolean;
  } = {},
) => {
  const { include = [], exclude = [], enableDefaultReport = true } = options;
  const el = e.target as Element;
  let matchDataReport = null;
  let matchDefaultReport = null;
  let isExclude = false;
  let isInclude = !include.length;
  let action = '';
  let params = {};

  let cur = el;
  while (cur !== null) {
    // 缓存离target最近的默认上报节点
    if (enableDefaultReport && !matchDefaultReport && DEFAULT_REPORT_NODE.includes(cur.nodeName)) {
      matchDefaultReport = cur;
    }

    // 是否在默认上报范围中
    if (include.length && matchesOne(cur, include)) {
      isInclude = true;
    }

    // 是否排除默认上报
    if (exclude.length && matchesOne(cur, exclude)) {
      isExclude = true;
    }

    if (typeof cur.hasAttributes === 'function' && cur.hasAttributes()) {
      // 有显式埋点节点优先级最高
      action = cur.getAttribute(ACTION_TAG) || cur.getAttribute(ACTION_GLOBAL_TAG) || '';
      if (action) {
        matchDataReport = cur;

        if (cur.hasAttribute(ACTION_GLOBAL_TAG)) {
          params = { is_global: true };
        }
        const extra = cur.getAttribute(ACTION_EXTRA_TAG);
        if (extra) {
          params = { ...params, ...paramsParser(extra) };
        }

        break;
      }
    }

    cur = cur.parentNode as Element;
  }

  // 没有上报节点
  if (!matchDataReport && !matchDefaultReport) {
    return;
  }

  // 没有显示埋点，但有默认上报节点
  if (!matchDataReport && matchDefaultReport && isInclude && !isExclude) {
    action = getDomPath(matchDefaultReport);
  }

  if (!action) {
    return;
  }

  const targetNode = (matchDataReport || matchDefaultReport) as Element;
  if (targetNode.textContent) {
    params = merge(params, { extra_data: { label: targetNode.textContent } });
  }

  return { action, ...params };
};
